import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Category from '@mui/icons-material/CategoryOutlined';
import ContentCopy from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { monoSx } from '../dashboard/styles';
import FormField from './FormField';
import OrderItemRow, { OrderItemHandlers } from './OrderItemRow';
import UnitSelect from './UnitSelect';
import { DIMENSION_UNITS, PackageDraft, WEIGHT_UNITS, defaultPackageRef, isPositive, plural } from './labelForm';
import { denseInputSx, monoInputSx, numericInputProps, rowWellSx } from './styles';

const numberInputProps = { ...numericInputProps, style: { ...numericInputProps.style, textAlign: 'center' as const } };

/** Outlined 40px box hosting several borderless inputs (L × W × H + unit). */
const measureBoxSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  height: 40,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  '&:focus-within': { borderColor: 'primary.main', boxShadow: (theme: Theme) => `inset 0 0 0 1px ${theme.palette.primary.main}` },
  '& .MuiInputBase-root:not(.MuiNativeSelect-root)': { flex: 1, minWidth: 0, height: '100%', fontSize: 14 },
};

const fieldLabelSx: SxProps<Theme> = { fontSize: 12, fontWeight: 600, color: 'text.secondary' };

const times = (
  <Box component="span" aria-hidden sx={{ color: 'text.secondary', fontSize: 13 }}>
    ×
  </Box>
);

type DimensionField = 'length' | 'width' | 'height';
const DIMENSIONS: ReadonlyArray<[DimensionField, string, string]> = [
  ['length', 'L', 'length'],
  ['width', 'W', 'width'],
  ['height', 'H', 'height'],
];

export interface PackageCardHandlers extends OrderItemHandlers {
  onChange: (key: string, patch: Partial<PackageDraft>) => void;
  onDuplicate: (key: string) => void;
  onRemove: (key: string) => void;
  onAddItem: (packageKey: string) => void;
}

interface PackageCardProps extends PackageCardHandlers {
  pkg: PackageDraft;
  index: number;
  total: number;
  removable: boolean;
}

/** One package: identity + measurements, and the order items packed inside it. */
const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  index,
  total,
  removable,
  onChange,
  onDuplicate,
  onRemove,
  onAddItem,
  ...itemHandlers
}) => {
  const n = index + 1;
  const { key, items } = pkg;
  const units = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const hasHazmat = items.some((item) => item.hazmat);

  return (
    <Box
      role="group"
      aria-label={`Package ${n}`}
      sx={(theme) => ({
        border: 1,
        borderColor: hasHazmat ? alpha(theme.palette.error.main, 0.4) : 'divider',
        borderRadius: 3.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          pl: 2,
          pr: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: 1,
          borderColor: 'divider',
        })}
      >
        <Box
          aria-hidden
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 14,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {n}
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Typography component="h3" sx={{ fontWeight: 600, fontSize: 15 }}>
            Package {n} of {total}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            <Box component="span" sx={monoSx}>
              {pkg.referenceId.trim() || defaultPackageRef(index)}
            </Box>
            {` · ${plural(items.length, 'item')}`}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Button
            onClick={() => onDuplicate(key)}
            aria-label={`Duplicate package ${n}`}
            startIcon={<ContentCopy />}
            sx={{ height: 36, px: 1.5, borderRadius: 18, fontSize: 13, fontWeight: 600, color: 'text.secondary' }}
          >
            Duplicate
          </Button>
          {removable && (
            <Tooltip title="Remove package">
              <IconButton
                aria-label={`Remove package ${n}`}
                onClick={() => onRemove(key)}
                sx={(theme) => ({ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) } })}
              >
                <DeleteOutline sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '14px 12px' }}>
        <FormField label="Package reference ID">
          <OutlinedInput
            value={pkg.referenceId}
            placeholder={defaultPackageRef(index)}
            onChange={(e) => onChange(key, { referenceId: e.target.value })}
            inputProps={{ 'aria-label': `Package ${n} reference ID` }}
            sx={[denseInputSx, monoInputSx] as SxProps<Theme>}
          />
        </FormField>
        <FormField label="Partner package ID" optional>
          <OutlinedInput
            value={pkg.partnerPackageId}
            onChange={(e) => onChange(key, { partnerPackageId: e.target.value })}
            inputProps={{ 'aria-label': `Package ${n} partner package ID` }}
            sx={[denseInputSx, monoInputSx] as SxProps<Theme>}
          />
        </FormField>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          <Box component="span" sx={fieldLabelSx}>
            Weight
            <Box component="span" aria-hidden sx={{ color: 'error.main' }}>
              {' *'}
            </Box>
          </Box>
          <OutlinedInput
            value={pkg.weight}
            placeholder="0"
            error={!isPositive(pkg.weight)}
            onChange={(e) => onChange(key, { weight: e.target.value })}
            inputProps={{ ...numberInputProps, 'aria-label': `Package ${n} weight` }}
            endAdornment={
              <UnitSelect
                aria-label={`Package ${n} weight unit`}
                value={pkg.weightUnit}
                options={WEIGHT_UNITS}
                onChange={(weightUnit) => onChange(key, { weightUnit })}
              />
            }
            sx={[denseInputSx, { pr: 0, overflow: 'hidden' }] as SxProps<Theme>}
          />
        </Box>
        <Box role="group" aria-label={`Package ${n} dimensions`} sx={{ gridColumn: '1 / -1', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box component="span" aria-hidden sx={fieldLabelSx}>
            Dimensions · L × W × H
          </Box>
          <Box sx={measureBoxSx}>
            {DIMENSIONS.map(([field, placeholder, name], i) => (
              <React.Fragment key={field}>
                {i > 0 && times}
                <InputBase
                  value={pkg[field]}
                  placeholder={placeholder}
                  onChange={(e) => onChange(key, { [field]: e.target.value })}
                  inputProps={{ ...numberInputProps, 'aria-label': `Package ${n} ${name}` }}
                />
              </React.Fragment>
            ))}
            <UnitSelect
              aria-label={`Package ${n} dimension unit`}
              value={pkg.dimensionUnit}
              options={DIMENSION_UNITS}
              onChange={(dimensionUnit) => onChange(key, { dimensionUnit })}
            />
          </Box>
        </Box>
      </Box>

      <Box
        role="group"
        aria-label={`Order items in package ${n}`}
        sx={[rowWellSx, { mx: 2, mb: 2, p: 1.5, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1 }] as SxProps<Theme>}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
          <Category sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Order items in this package</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{plural(units, 'unit')}</Typography>
        </Box>
        {items.map((item, i) => (
          <OrderItemRow key={item.key} item={item} name={`Package ${n} item ${i + 1}`} removable={items.length > 1} {...itemHandlers} />
        ))}
        <Button
          onClick={() => onAddItem(key)}
          startIcon={<Add />}
          sx={{ alignSelf: 'flex-start', height: 34, px: 1.5, borderRadius: 17, fontSize: 13, fontWeight: 600, color: 'primary.dark', '&:hover': { bgcolor: 'brand.container' } }}
        >
          Add item to package {n}
        </Button>
      </Box>
    </Box>
  );
};

export default memo(PackageCard);
